import { Injectable, inject } from '@angular/core';
import { ActiveCompanyService } from './active-company.service';
import { ModuleAccessService } from './module-access.service';

/** Skip repeat shell network work within this window when a company is already active. */
const SHELL_READY_TTL_MS = 60_000;

/**
 * Single in-flight shell bootstrap for toolbar + nav + guards.
 * Avoids duplicate /companies + module refreshes on main layout mount.
 */
@Injectable({ providedIn: 'root' })
export class ShellBootstrapService {
  private readonly activeCompany = inject(ActiveCompanyService);
  private readonly moduleAccess = inject(ModuleAccessService);

  private inFlight: Promise<void> | null = null;
  private lastCompletedAt = 0;

  /**
   * Ensure companies + module context are ready.
   * Dedupes concurrent callers; skips network if completed within TTL and an active company exists.
   */
  ensureReady(options?: { force?: boolean }): Promise<void> {
    const force = options?.force === true;
    const now = Date.now();
    if (
      !force &&
      this.lastCompletedAt > 0 &&
      now - this.lastCompletedAt < SHELL_READY_TTL_MS &&
      this.activeCompany.getActiveOrFallbackCompanyId()
    ) {
      return Promise.resolve();
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = (async () => {
      try {
        this.activeCompany.loadFromStorage();
        await this.activeCompany.refreshCompaniesAndEnsureActive();
        await this.moduleAccess.refreshFromActiveCompany();
        this.lastCompletedAt = Date.now();
      } finally {
        this.inFlight = null;
      }
    })();
    return this.inFlight;
  }

  /** Call after logout / company switch that must force a reload. */
  invalidate(): void {
    this.lastCompletedAt = 0;
    this.inFlight = null;
  }
}
